import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ExhibitLightbox.css';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const WHEEL_SCALE_STEP = 0.15;

const distanceBetween = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
const midpointOf = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

const clampTransform = (next) => ({
  ...next,
  scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale)),
});

/**
 * Full-screen tap-to-zoom viewer for exam exhibit images. Images across exams
 * arrive with inconsistent source dimensions and inconsistent internal scale,
 * so there's no single deterministic inline size that reads well everywhere.
 * Instead of guessing, the inline image stays small/consistent and the user
 * zooms/pans here to whatever scale makes the exhibit legible.
 */
function ExhibitLightbox({ url, alt, title, onClose }) {
  const bodyRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const dragState = useRef(null); // { startX, startY, startTX, startTY }
  const pinchState = useRef(null); // { startDist, startScale, startTX, startTY, midX, midY }
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const body = bodyRef.current;
    if (!body) return;
    const rect = body.getBoundingClientRect();
    const anchorX = e.clientX - rect.left;
    const anchorY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? -WHEEL_SCALE_STEP : WHEEL_SCALE_STEP;
    setTransform((prev) => {
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale + delta));
      const scaleFactor = newScale / prev.scale;
      return clampTransform({
        scale: newScale,
        x: anchorX - (anchorX - prev.x) * scaleFactor,
        y: anchorY - (anchorY - prev.y) * scaleFactor,
      });
    });
  }, []);

  const toggleDoubleTapZoom = useCallback((clientX, clientY) => {
    const body = bodyRef.current;
    if (!body) return;
    const rect = body.getBoundingClientRect();
    const anchorX = clientX - rect.left;
    const anchorY = clientY - rect.top;
    setTransform((prev) => {
      if (prev.scale > MIN_SCALE + 0.01) {
        return { x: 0, y: 0, scale: MIN_SCALE };
      }
      const scaleFactor = DOUBLE_TAP_SCALE / prev.scale;
      return clampTransform({
        scale: DOUBLE_TAP_SCALE,
        x: anchorX - (anchorX - prev.x) * scaleFactor,
        y: anchorY - (anchorY - prev.y) * scaleFactor,
      });
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTX: transform.x,
      startTY: transform.y,
    };
  }, [transform.x, transform.y]);

  const handleMouseMove = useCallback((e) => {
    if (!dragState.current || transform.scale <= MIN_SCALE) return;
    const { startX, startY, startTX, startTY } = dragState.current;
    setTransform((prev) => ({
      ...prev,
      x: startTX + (e.clientX - startX),
      y: startTY + (e.clientY - startY),
    }));
  }, [transform.scale]);

  const endDrag = useCallback(() => {
    dragState.current = null;
  }, []);

  const handleDoubleClick = useCallback((e) => {
    toggleDoubleTapZoom(e.clientX, e.clientY);
  }, [toggleDoubleTapZoom]);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      dragState.current = null;
      const [t1, t2] = e.touches;
      const mid = midpointOf(t1, t2);
      pinchState.current = {
        startDist: distanceBetween(t1, t2),
        startScale: transform.scale,
        startTX: transform.x,
        startTY: transform.y,
        midX: mid.x,
        midY: mid.y,
      };
      return;
    }
    if (e.touches.length === 1) {
      pinchState.current = null;
      const touch = e.touches[0];
      dragState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTX: transform.x,
        startTY: transform.y,
      };

      const now = Date.now();
      const last = lastTapRef.current;
      const isDoubleTap = now - last.time < DOUBLE_TAP_MS
        && Math.hypot(touch.clientX - last.x, touch.clientY - last.y) < 30;
      lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };
      if (isDoubleTap) {
        toggleDoubleTapZoom(touch.clientX, touch.clientY);
        dragState.current = null;
        lastTapRef.current = { time: 0, x: 0, y: 0 };
      }
    }
  }, [transform.x, transform.y, transform.scale, toggleDoubleTapZoom]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && pinchState.current) {
      e.preventDefault();
      const [t1, t2] = e.touches;
      const dist = distanceBetween(t1, t2);
      const { startDist, startScale, startTX, startTY, midX, midY } = pinchState.current;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, startScale * (dist / startDist)));
      const scaleFactor = newScale / startScale;
      setTransform(clampTransform({
        scale: newScale,
        x: midX - (midX - startTX) * scaleFactor,
        y: midY - (midY - startTY) * scaleFactor,
      }));
      return;
    }
    if (e.touches.length === 1 && dragState.current && transform.scale > MIN_SCALE) {
      e.preventDefault();
      const touch = e.touches[0];
      const { startX, startY, startTX, startTY } = dragState.current;
      setTransform((prev) => ({
        ...prev,
        x: startTX + (touch.clientX - startX),
        y: startTY + (touch.clientY - startY),
      }));
    }
  }, [transform.scale]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) pinchState.current = null;
    if (e.touches.length === 0) dragState.current = null;
  }, []);

  // React 17+ registers wheel/touchstart/touchmove as passive on the root
  // container, so a React onWheel/onTouchMove prop can't preventDefault -
  // scroll-to-zoom would scroll the page behind the lightbox instead of
  // zooming, and one-finger pan would fight the page. Attach these natively
  // with { passive: false } instead. The handlers are useCallback'd on
  // `transform`, which changes on every touchmove during a pinch/drag, so
  // listing them in this effect's own deps would tear down and re-attach the
  // non-passive touchmove listener mid-gesture - on iOS Safari that can hand
  // the rest of the gesture back to the browser as a passive scroll, which is
  // the exact bug this exists to fix. Route through a ref instead so the
  // listeners attach once or resubscribe only when bodyRef.current itself changes.
  const handlersRef = useRef({ handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd });
  handlersRef.current = { handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd };

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const onWheel = (e) => handlersRef.current.handleWheel(e);
    const onTouchStart = (e) => handlersRef.current.handleTouchStart(e);
    const onTouchMove = (e) => handlersRef.current.handleTouchMove(e);
    const onTouchEnd = (e) => handlersRef.current.handleTouchEnd(e);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return createPortal(
    <div className="exhibit-lightbox-backdrop" role="presentation" onClick={onClose}>
      <div
        className="exhibit-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={title || alt || 'Exhibit image'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="exhibit-lightbox-header">
          <span className="exhibit-lightbox-title">{title || 'Exhibit'}</span>
          <button
            type="button"
            className="exhibit-lightbox-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
        <div
          ref={bodyRef}
          className="exhibit-lightbox-body"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onDoubleClick={handleDoubleClick}
          style={{ cursor: transform.scale > MIN_SCALE ? 'grab' : 'default' }}
        >
          <div
            className="exhibit-lightbox-canvas"
            style={{
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              transformOrigin: '0 0',
            }}
          >
            <img src={url} alt={alt || ''} draggable={false} />
          </div>
          <span className="exhibit-lightbox-hint">Pinch or scroll to zoom &middot; double-tap to reset</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ExhibitLightbox;
